/**
 * i18nUrls
 *
 * Helpers for language-prefixed URLs (`/en/search/paris/lyon`).
 * French is served at the unprefixed URL and never appears as a prefix
 * (`/fr/*` is 301-redirected by nginx, see docker/nginx/nginx.conf).
 *
 * This module must stay dependency-free: it is imported by `utils/history`,
 * which is loaded before the store and the translation bundle. The JSON below
 * is inert data, not a module: it carries no import of its own.
 */
import indexablePaths from '../other/i18nIndexablePaths.json';

/**
 * Locale served at the unprefixed URL. Must equal DEFAULT_LOCALE in src/i18n.js.
 */
export const URL_DEFAULT_LOCALE = 'fr';

/**
 * Locales exposed as URL prefixes. Subset of appLocales (src/i18n.js).
 * Routes, history prefixing and the translation banner follow automatically.
 */
export const URL_LOCALES = ['en', 'it', 'es', 'de', 'nl'];

/**
 * Prefixed locales search engines may index on every page. The default locale
 * (french, unprefixed) is always indexable too.
 * To open a language site-wide, add it here.
 */
export const INDEXABLE_URL_LOCALES = ['en', 'es'];

/**
 * Locales opened page by page instead of site-wide, and the paths opened to
 * them (src/other/i18nIndexablePaths.json). Translating a language and
 * translating a page are two different jobs: these locales are complete on the
 * top pages and still partial elsewhere, so the pages that are ready get
 * indexed while the rest keeps its `noindex`.
 *
 * Everything not opened stays browsable but noindexed, and out of the hreflang
 * cluster — pointing hreflang at a noindexed page sends conflicting signals to
 * Google.
 */
const PARTIAL_URL_LOCALES: string[] = indexablePaths.locales;
const INDEXABLE_PATHS = new Set<string>(indexablePaths.paths);

/**
 * @description locales a given page may be indexed in — the hreflang cluster it
 * belongs to, the default locale excepted (it is always in, and never a prefix)
 * @param pathWithoutLang language-neutral path, as `stripLocale` returns it
 * @example '/company/carpooling' -> ['en', 'es', 'it', 'nl', 'de']
 * @example '/company/bus/itabus' -> ['en', 'es']
 */
export const indexableLocalesFor = (pathWithoutLang: string): string[] =>
  INDEXABLE_PATHS.has(pathWithoutLang)
    ? [...INDEXABLE_URL_LOCALES, ...PARTIAL_URL_LOCALES]
    : INDEXABLE_URL_LOCALES;

/**
 * @description whether a page may be indexed in a given locale. An absent or
 * default locale is the unprefixed french URL, always indexable.
 * @param pathWithoutLang language-neutral path, as `stripLocale` returns it
 */
export const isIndexable = (
  pathWithoutLang: string,
  locale?: string | null,
): boolean =>
  !locale ||
  locale === URL_DEFAULT_LOCALE ||
  indexableLocalesFor(pathWithoutLang).includes(locale);

const LANG_GROUP = URL_LOCALES.join('|');

// (?=/|$) so that '/entreprise' or '/de-anything' never match a locale prefix.
const LANG_PREFIX_REGEX = new RegExp(`^/(${LANG_GROUP})(?=/|$)`);

/**
 * @description extracts the locale prefix of a pathname
 * @example '/en/search/x' -> 'en' ; '/search/x' -> null ; '/entreprise' -> null
 */
export const getLocaleFromPathname = (pathname: string): string | null => {
  const match = pathname?.match(LANG_PREFIX_REGEX);

  return match ? match[1] : null;
};

/**
 * @description removes the locale prefix of a pathname.
 * Use this before comparing a pathname to NAVIGATION constants — the address
 * bar (and the Redux router state) keeps the prefix.
 * @example '/en/search/x' -> '/search/x' ; '/en' -> '/' ; '/search/x' -> '/search/x'
 */
export const stripLocale = (pathname: string): string =>
  pathname.replace(LANG_PREFIX_REGEX, '') || '/';

/**
 * @description re-targets a path (with optional ?query/#hash) to a locale.
 * Any existing prefix is stripped first; the default locale yields the bare path.
 * Non-absolute paths are returned untouched.
 * @example localizePath('/search/x?d=1', 'en') -> '/en/search/x?d=1'
 * @example localizePath('/en/search/x', 'fr') -> '/search/x'
 */
export const localizePath = (path: string, locale: string): string => {
  if (!path?.startsWith('/')) return path;

  const separatorIndex = path.search(/[?#]/);
  const pathname = separatorIndex === -1 ? path : path.slice(0, separatorIndex);
  const rest = separatorIndex === -1 ? '' : path.slice(separatorIndex);
  const stripped = stripLocale(pathname);

  if (!locale || !URL_LOCALES.includes(locale)) return `${stripped}${rest}`;

  return `/${locale}${stripped === '/' ? '' : stripped}${rest}`;
};

/**
 * @description decorates a react-router path pattern with the optional locale prefix
 * @example '/search/:o/:d' -> '/:lang(en)?/search/:o/:d'
 * @example '/' -> ['/:lang(en)', '/'] (array path: avoids optional-param-at-root + exact pitfalls)
 */
export const localized = (path: string): string | string[] =>
  path === '/'
    ? [`/:lang(${LANG_GROUP})`, '/']
    : `/:lang(${LANG_GROUP})?${path}`;

/**
 * Hosts that serve this very app: production and staging. Matching is on the
 * exact host — `api.tictactrip.eu` and `blog.tictactrip.eu` are other
 * services, and turning their links into local paths would break them.
 */
const SELF_HOSTS = [
  'tictactrip.eu',
  'www.tictactrip.eu',
  'comparatrip.eu',
  'www.comparatrip.eu',
];

/**
 * Paths served next to the app rather than by it: nginx proxies `/blog/` to
 * the blog backend and `/assets` holds static files (see docker/nginx).
 * They must keep their bare path — a language prefix would miss those nginx
 * locations, fall through to the SPA and 404.
 */
const NON_LOCALIZED_PREFIXES = ['/blog', '/assets'];

const isNonLocalized = (path: string): boolean =>
  NON_LOCALIZED_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );

/**
 * @description turns a link into a path on the current host when it points at
 * this app, whichever domain it was written against. Airtable content hardcodes
 * `https://www.tictactrip.eu/...`, which would otherwise send a visitor
 * browsing staging (comparatrip.eu) straight to production.
 * Returns null for genuinely external links.
 */
const toSameHostPath = (href: string): string | null => {
  if (href.startsWith('/')) return href;

  const url = href.match(/^https?:\/\/([^/?#]+)([/?#].*)?$/i);

  if (!url || !SELF_HOSTS.includes(url[1].toLowerCase())) return null;

  return url[2] || '/';
};

/**
 * @description re-targets an internal href to the current host and language,
 * so raw <a> links stay on the site the visitor is browsing and keep their
 * language (router links go through the history wrapper instead). Absolute
 * links to our own domains become paths; external URLs, already-prefixed
 * hrefs and paths served outside the SPA are left untouched.
 */
export const withCurrentLocale = (href?: string): string | undefined => {
  const path = href === undefined ? null : toSameHostPath(href);

  if (path === null) return href;

  const currentLang = getLocaleFromPathname(window.location.pathname);

  if (isNonLocalized(path) || getLocaleFromPathname(path) !== null) return path;

  return localizePath(path, currentLang || URL_DEFAULT_LOCALE);
};

/**
 * @description re-targets the internal links embedded in a CMS HTML string,
 * so they keep the current host and language. Airtable content ships raw
 * `<a href="/search/lyon/annecy">` and `<a href="https://www.tictactrip.eu/...">`
 * links: they never go through the Link components, and would otherwise drop a
 * visitor reading /en on staging back onto the french production site.
 * External links and already-prefixed hrefs are left alone.
 */
export const withCurrentLocaleInHtml = (html?: string): string | undefined => {
  if (!html) return html;

  return html.replace(
    // `\s*=\s*`: CMS content ships `href = "…"` too, and those links must be
    // localized like the others (the build normalizes the quoting, not the
    // spacing — see utils/pageGen/sanitizeLinks.js).
    /(<a\b[^>]*?\shref\s*=\s*")([^"]*)(")/gi,
    (match, before, href, after) => {
      const localized = withCurrentLocale(href);

      return localized === href ? match : `${before}${localized}${after}`;
    },
  );
};

/**
 * @description hreflang link descriptors for a language-neutral path.
 * Only the locales this very page is indexable in are listed: a hreflang entry
 * pointing at a noindexed page is a conflicting signal and gets dropped by
 * Google anyway. Two pages therefore need not advertise the same cluster.
 */
export const buildAlternateLinks = (
  pathWithoutLang: string,
  origin: string = `${window.location.protocol}//${window.location.host}`,
): { hrefLang: string; href: string }[] => [
  ...[URL_DEFAULT_LOCALE, ...indexableLocalesFor(pathWithoutLang)].map(
    (locale) => ({
      hrefLang: locale,
      href: `${origin}${localizePath(pathWithoutLang, locale)}`,
    }),
  ),
  {
    hrefLang: 'x-default',
    href: `${origin}${localizePath(pathWithoutLang, URL_DEFAULT_LOCALE)}`,
  },
];

/**
 * @description name of the locale variant of a generated page JSON.
 * The build writes '<slug>.json' for French and '<slug>.<locale>.json' for the
 * others; the containers import them through a webpack lazy context, so the
 * import() itself has to stay at the call site — only the name travels.
 */
export const localizedJsonFile = (
  baseFile: string,
  locale?: string | null,
): string =>
  locale ? baseFile.replace(/\.json$/, `.${locale}.json`) : baseFile;
